<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :adjustColumnsWidth="adjustColumnsWidthConfig"
      :pdfButton="pdfButtonConfig"
      :csvButton="csvButtonConfig"
      :clipboardButton="clipboardButtonConfig"
      :printButton="printButtonConfig"
      data-testid="servicos-categorias-table"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="handleRowClick"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

interface CategoriaRow {
  servicoscategoria_id: number | string;
  servicoscategoria_nome?: string;
  servicoscategoria_prazo_retorno_dias?: number;
  servicoscategoria_prazo_garantia_dias?: number;
  servicoscategoria_produtos_associados?: number | string;
  [key: string]: unknown;
}

interface CellRenderParams {
  value: unknown;
}

interface DataTableExposed {
  loadData: () => void;
}

const { t } = useI18n();

const datatableRef = ref<DataTableExposed | null>(null);

const emit = defineEmits<{
  edit: [categoriaId: number | string];
}>();

const endpoint = computed<string>(() => '/index.php/config/servicos_categorias/carrega_categorias');

const tableColumns = computed(() => {
  return [
    {
      data: 'servicoscategoria_id',
      title: t('ID'),
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id'
    },
    {
      data: 'servicoscategoria_nome',
      title: t('Nome'),
      width: 300,
      resizable: true,
      sortable: true,
      filterable: true
    },
    {
      data: 'servicoscategoria_prazo_retorno_dias',
      title: t('Prazo de Retorno (dias)'),
      width: 180,
      resizable: true,
      sortable: true,
      renderType: 'number'
    },
    {
      data: 'servicoscategoria_prazo_garantia_dias',
      title: t('Prazo de Garantia (dias)'),
      width: 180,
      resizable: true,
      sortable: true,
      renderType: 'number'
    },
    {
      data: 'servicoscategoria_produtos_associados',
      title: t('Produtos Associados'),
      width: 150,
      resizable: true,
      sortable: true,
      cellRenderer: (params: CellRenderParams) => {
        return params.value === 1 || params.value === '1' ? t('Sim') : t('Não');
      }
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaServicosCategorias',
    title: t('Categorias dos Serviços'),
    filename: `servicos_categorias_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    height: '500px',
    footer: false,
    rowSelection: 'single',
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.servicoscategoria_id;
      return id == null ? '' : `servicos-categorias-row-${id}`;
    }
  };
});

const filterConfig = computed(() => ({
  enabled: true
}));

const refreshButtonConfig = computed(() => ({
  show: true
}));

const adjustColumnsWidthConfig = computed(() => ({
  show: true
}));

const pdfButtonConfig = computed(() => ({
  show: true
}));

const csvButtonConfig = computed(() => ({
  show: true
}));

const clipboardButtonConfig = computed(() => ({
  show: true
}));

const printButtonConfig = computed(() => ({
  show: true
}));

const loadData = (): void => {
  log('ServicosCategoriasTable', 'loadData', 'Triggering data reload');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: CategoriaRow[] | undefined): void => {
  log('ServicosCategoriasTable', 'handleDataLoaded', 'Data loaded successfully', data?.length);
};

const handleDataError = (error: unknown): void => {
  log('ServicosCategoriasTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar categorias'));
};

const handleRowClick = (event?: { data?: CategoriaRow }): void => {
  const rowData = event?.data ?? null;
  log('ServicosCategoriasTable', 'handleRowClick', 'Row clicked', rowData);
  if (rowData && rowData.servicoscategoria_id) {
    emit('edit', rowData.servicoscategoria_id);
  }
};

const handleEditClick = (rowData: number | string): void => {
  log('ServicosCategoriasTable', 'handleEditClick', 'Edit clicked', rowData);
  if (rowData) {
    // rowData is already the ID from clickable_id renderType
    emit('edit', rowData);
  }
};

const handleRefresh = (): void => {
  log('ServicosCategoriasTable', 'handleRefresh', 'Refresh event received');
  loadData();
};

onMounted(() => {
  log('ServicosCategoriasTable', 'mounted', 'Component mounted');
  eventBus.on('servicos-categorias.refresh', handleRefresh);
});

onBeforeUnmount(() => {
  log('ServicosCategoriasTable', 'beforeUnmount', 'Component unmounting');
  eventBus.off('servicos-categorias.refresh', handleRefresh);
});

defineExpose({
  loadData
});
</script>

<style scoped>
/* No additional styles needed */
</style>
